import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp
} from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, Button, TextField, MenuItem, Select,
  IconButton, Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Tooltip, Grid, ToggleButtonGroup, ToggleButton, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Preview';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LagbasLogo from '../components/LagbasLogo';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const ZONE_COLORS = {
  HUVUDANSLAG: '#FF6B00',
  SIDOFÄLTET:  '#2196F3',
  'SIDO-Ö':    '#1565C0',
  'SIDO-N':    '#42a5f5',
  'SIDFOT-1':  '#2e7d32',
  'SIDFOT-2':  '#388e3c',
  'SIDFOT-3':  '#4caf50',
  'SIDFOT-4':  '#66bb6a',
  SIDFOT:      '#4caf50', // legacy
};

const ZONE_GROUPS = [
  {
    id: 'HUVUD', label: 'Huvudanslag', color: '#FF6B00',
    zones: ['HUVUDANSLAG'],
  },
  {
    id: 'SIDO', label: 'Sidofältet', color: '#2196F3',
    zones: ['SIDOFÄLTET', 'SIDO-Ö', 'SIDO-N', 'SIDO-3'],
  },
  {
    id: 'SIDFOT', label: 'Sidfot', color: '#4caf50',
    zones: ['SIDFOT', 'SIDFOT-1', 'SIDFOT-2', 'SIDFOT-3', 'SIDFOT-4'],
  },
];

function getSlideThumbnail(sd) {
  if (!sd) return null;
  const { type, content = {} } = sd;
  if (type === 'album' && content.images?.[0]) return { src: content.images[0], count: content.images.length };
  if (type === 'media' && content.url) {
    const isImg = !['mp4','mov','webm','pdf'].some(e => (content.fileType || content.url || '').toLowerCase().includes(e));
    if (isImg) return { src: content.url, count: null };
  }
  if (type === 'image_url' && content.url) return { src: content.url, count: null };
  if (type === 'text' && content.image) return { src: content.image, count: null };
  return null;
}

export const ZONE_OPTIONS = [
  { value: 'HUVUDANSLAG', label: 'Huvudanslag' },
  { value: 'SIDOFÄLTET',  label: 'Sidofält (hel)' },
  { value: 'SIDO-Ö',      label: 'Sidofält – övre halva' },
  { value: 'SIDO-N',      label: 'Sidofält – nedre halva' },
  { value: 'SIDO-3',      label: 'Sidofält – tredje rad' },
  { value: 'SIDFOT-1',    label: 'Sidfot – kolumn 1' },
  { value: 'SIDFOT-2',    label: 'Sidfot – kolumn 2' },
  { value: 'SIDFOT-3',    label: 'Sidfot – kolumn 3' },
  { value: 'SIDFOT-4',    label: 'Sidfot – kolumn 4' },
];

const CAST_THEMES = [
  { id: 'tikab',   label: 'Tikab Orange',  footerBg: '#1a1a1a', accent: '#FF6B00' },
  { id: 'nordic',  label: 'Nordic Blue',   footerBg: '#0d1b2a', accent: '#2196F3' },
  { id: 'dark',    label: 'Minimal Dark',  footerBg: '#0d0d0d', accent: '#aaaaaa' },
  { id: 'light',   label: 'Light Clean',   footerBg: '#f5f5f5', accent: '#333333' },
  { id: 'forest',  label: 'Forest',        footerBg: '#0a1f15', accent: '#4caf50' },
];

const SLIDE_TYPES = [
  { section: 'TEXT & BILD', items: [
    { type: 'text', label: 'Text & Bild', emoji: '📝' },
    { type: 'album', label: 'Bildalbum / Presentation', emoji: '🗂️' },
    { type: 'media', label: 'Enskild bild / video / PDF', emoji: '🖼️' },
    { type: 'image_url', label: 'Bild via URL', emoji: '🔗' },
    { type: 'video', label: 'Video / YouTube', emoji: '▶️' },
    { type: 'weather', label: 'Väder', emoji: '⛅' },
    { type: 'weather_map', label: 'Väderkarta', emoji: '🗺️' },
    { type: 'traffic', label: 'Avgångar (kollektivtrafik)', emoji: '🚌' },
    { type: 'rss', label: 'RSS-nyheter', emoji: '📰' },
    { type: 'url', label: 'Webbsida / Kalender', emoji: '🌐' },
    { type: 'world_clock', label: 'Världsklocka', emoji: '🕐' },
    { type: 'date_tearoff', label: 'Datumlapp', emoji: '📅' },
    { type: 'menu', label: 'Veckans meny', emoji: '🍽️' },
  ]},
  { section: 'INTEGRATIONER', items: [
    { type: 'emoji', label: 'Emoji / Ikon', emoji: '😀' },
    { type: 'google_calendar', label: 'Google Kalender', emoji: '📅' },
    { type: 'ms_calendar', label: 'Microsoft Kalender', emoji: '🗓️' },
    { type: 'power_bi', label: 'Power BI', emoji: '📊' },
    { type: 'sharepoint', label: 'SharePoint', emoji: '🗂️' },
  ]},
  { section: 'SOCIALA MEDIER', items: [
    { type: 'social_facebook', label: 'Facebook', emoji: '📘' },
    { type: 'social_instagram', label: 'Instagram', emoji: '📸' },
    { type: 'social_linkedin', label: 'LinkedIn', emoji: '💼' },
  ]},
  { section: 'DELA & INTERAKTION', items: [
    { type: 'qr_display', label: 'QR-kod – dela bildspel', emoji: '📲' },
    { type: 'birthday',   label: 'Födelsedagshälsning',   emoji: '🎂' },
  ]},
  { section: 'FRÅN LAGBASN', items: [
    { type: 'lagbasn_reference', label: 'Referensprojekt', emoji: '🏗️' },
    { type: 'lagbasn_leads', label: 'Aktiva anbud', emoji: '📊' },
    { type: 'lagbasn_wins', label: 'Vunna affärer 🏆', emoji: '🏆' },
  ]},
];

function SlideTypePicker({ open, onClose, onSelect }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor:'#1a1a1a', color:'white' }}>
        Välj Anslags-typ
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {SLIDE_TYPES.map(section => (
          <Box key={section.section} sx={{ mb: 3 }}>
            <Typography variant="overline" sx={{ color:'#FF6B00', fontWeight:'bold', letterSpacing: 2 }}>
              {section.section}
            </Typography>
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              {section.items.map(item => (
                <Grid item xs={6} sm={4} key={item.type}>
                  <Paper variant="outlined" sx={{ p: 2, cursor:'pointer', textAlign:'center',
                    '&:hover':{ borderColor:'#FF6B00', bgcolor:'rgba(255,107,0,0.05)' } }}
                    onClick={() => { onSelect(item.type); onClose(); }}>
                    <Typography sx={{ fontSize:'2rem', mb: 0.5 }}>{item.emoji}</Typography>
                    <Typography variant="body2" fontWeight="bold">{item.label}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Avbryt</Button>
      </DialogActions>
    </Dialog>
  );
}

function SlidePreviewDialog({ open, onClose, slide, slideRef }) {
  if (!slide) return null;
  const { type, content = {} } = slide;
  // Simple visual preview — full rendering requires all Firebase context
  // so we show a representative summary; complex live slides open in new tab.
  const getBg = () => {
    if (content.bgPresetId) {
      const PRESETS = [
        { id:'grad-tikab', css:'linear-gradient(135deg,#FF6B00 0%,#cc4400 100%)' },
        { id:'grad-night', css:'linear-gradient(135deg,#0f172a 0%,#1e293b 100%)' },
        { id:'grad-ocean', css:'linear-gradient(135deg,#0ea5e9 0%,#1565C0 100%)' },
        { id:'grad-forest', css:'linear-gradient(135deg,#064e3b 0%,#059669 100%)' },
        { id:'grad-purple', css:'linear-gradient(135deg,#4c1d95 0%,#7c3aed 100%)' },
        { id:'grad-sunset', css:'linear-gradient(135deg,#ea580c 0%,#db2777 100%)' },
        { id:'grad-steel', css:'linear-gradient(135deg,#1e293b 0%,#334155 100%)' },
        { id:'grad-gold', css:'linear-gradient(135deg,#78350f 0%,#d97706 100%)' },
        { id:'solid-black', css:'#111111' }, { id:'solid-white', css:'#ffffff' },
      ];
      return PRESETS.find(b => b.id === content.bgPresetId)?.css || '#111';
    }
    return '#111';
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor:'#1a1a1a', color:'white', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span>Förhandsgranskning – {type}</span>
        <Tooltip title="Öppna i fullskärm (ny flik)">
          <Button size="small" startIcon={<OpenInNewIcon />}
            onClick={() => window.open(`/castn/preview/${slideRef?.slideId}`, '_blank')}
            sx={{ color:'#FF6B00' }}>
            Fullskärm
          </Button>
        </Tooltip>
      </DialogTitle>
      <DialogContent sx={{ p: 0, bgcolor:'#111' }}>
        {/* 16:9 preview box */}
        <Box sx={{ position:'relative', paddingTop:'56.25%', width:'100%', overflow:'hidden' }}>
          <Box sx={{ position:'absolute', inset:0, background: getBg(), display:'flex', flexDirection:'column',
            alignItems:'center', justifyContent:'center', p: 4, gap: 2 }}>
            {type === 'text' && (
              <>
                {content.image && <img src={content.image} alt="" style={{ maxHeight:120, maxWidth:'60%', objectFit:'contain', borderRadius:4 }} />}
                {content.title && <Typography variant="h4" fontWeight="bold" sx={{ color: getBg()==='#ffffff'?'#111':'#fff', textAlign:'center' }}>{content.title}</Typography>}
                {content.body && <Typography sx={{ color: getBg()==='#ffffff'?'#444':'#ccc', textAlign:'center', maxWidth:'80%' }}>{content.body}</Typography>}
              </>
            )}
            {type === 'album' && (
              content.images?.length > 0
                ? (
                  <Box sx={{ display:'flex', flexWrap:'wrap', gap: 0.75, justifyContent:'center', maxWidth:'90%' }}>
                    {content.images.slice(0,6).map((img, i) => (
                      <Box key={i} sx={{ width: 80, height: 52, borderRadius:1, overflow:'hidden', position:'relative' }}>
                        <img src={img} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                        <Box sx={{ position:'absolute', top:2, left:2, bgcolor:'rgba(0,0,0,0.6)', color:'white', fontSize:'0.55rem', px:0.5, borderRadius:0.5 }}>{i+1}</Box>
                      </Box>
                    ))}
                    {content.images.length > 6 && <Typography sx={{ color:'#aaa', alignSelf:'center' }}>+{content.images.length - 6} till</Typography>}
                  </Box>
                )
                : <Typography sx={{ color:'#555' }}>Inga bilder i albumet</Typography>
            )}
            {(type === 'media' || type === 'image_url') && (
              (content.url || content.image)
                ? <img src={content.url || content.image} alt="" style={{ maxHeight:240, maxWidth:'80%', objectFit:'contain' }} />
                : <Typography sx={{ color:'#555' }}>Ingen media vald</Typography>
            )}
            {type === 'weather' && (
              <>
                <Typography sx={{ fontSize:'4rem' }}>⛅</Typography>
                <Typography variant="h4" sx={{ color:'#fff' }}>{content.city || 'Stockholm'}</Typography>
                <Typography sx={{ color:'#aaa' }}>Väder laddas vid visning</Typography>
              </>
            )}
            {type === 'traffic' && (
              <>
                <Typography sx={{ fontSize:'3rem' }}>🚌</Typography>
                <Typography variant="h5" sx={{ color:'#fff' }}>
                  {(content.stops || []).map(s => s.name).join(' · ') || 'Avgångar'}
                </Typography>
                <Typography sx={{ color:'#aaa' }}>Avgångar laddas vid visning</Typography>
              </>
            )}
            {type === 'world_clock' && (() => {
              const zones = content.zones || [{label:'Stockholm',tz:'Europe/Stockholm'}];
              const now = new Date();
              return zones.map((z, i) => {
                const t = now.toLocaleTimeString('sv-SE', { timeZone: z.tz, hour:'2-digit', minute:'2-digit' });
                return (
                  <Box key={i} sx={{ textAlign:'center', px: 2 }}>
                    <Typography sx={{ color:'#FF6B00', fontSize:'0.9rem', letterSpacing:2 }}>{z.label}</Typography>
                    <Typography sx={{ color:'#fff', fontSize:'2.5rem', fontWeight:'bold', fontVariantNumeric:'tabular-nums' }}>{t}</Typography>
                  </Box>
                );
              });
            })()}
            {type === 'rss' && (
              <>
                <Typography sx={{ fontSize:'3rem' }}>📰</Typography>
                <Typography variant="h5" sx={{ color:'#fff' }}>{content.title || 'RSS-nyheter'}</Typography>
                <Typography sx={{ color:'#aaa', fontSize:'0.8rem' }}>{content.url}</Typography>
              </>
            )}
            {type === 'weather_map' && (
              <iframe
                src={`https://embed.windy.com/embed2.html?lat=${content.lat||59.33}&lon=${content.lon||18.07}&zoom=${content.zoom||5}&level=surface&overlay=${content.layer||'wind'}&product=ecmwf&menu=&message=true&type=map&location=coordinates&metricWind=m%2Fs&metricTemp=%C2%B0C`}
                title="Weather Map Preview"
                style={{ position:'absolute', inset:0, width:'100%', height:'100%', border:'none' }}
              />
            )}
            {type === 'video' && (() => {
              const ytMatch = (content.url||'').match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([A-Za-z0-9_-]{11})/);
              const ytId = content.youtubeId || (ytMatch ? ytMatch[1] : null);
              if (ytId) return <iframe src={`https://www.youtube.com/embed/${ytId}?autoplay=0`} title="Video"
                style={{ position:'absolute', inset:0, width:'100%', height:'100%', border:'none' }} allow="autoplay" />;
              return <Typography sx={{ color:'#aaa' }}>Video-URL: {content.url}</Typography>;
            })()}
            {type === 'url' && (
              <iframe src={content.url} title="URL Preview"
                style={{ position:'absolute', inset:0, width:'100%', height:'100%', border:'none' }} />
            )}
            {!['text','media','image_url','weather','traffic','world_clock','rss','weather_map','video','url'].includes(type) && (
              <>
                <Typography sx={{ fontSize:'3rem' }}>📄</Typography>
                <Typography sx={{ color:'#aaa', textAlign:'center' }}>
                  Förhandsgranskning ej tillgänglig för denna typ.<br/>
                  Klicka Fullskärm för live-rendering.
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ bgcolor:'#1a1a1a' }}>
        <Button onClick={onClose} sx={{ color:'#aaa' }}>Stäng</Button>
      </DialogActions>
    </Dialog>
  );
}

function SortableSlideRow({ sr, slideDetails, getSlideEmoji, navigate, selectedCastId, handleDeleteSlide, handleToggleActive, handleChangeZone, onPreview }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sr.slideId });
  const sd = slideDetails[sr.slideId];
  const isActive = sd?.active !== false;
  const thumb = getSlideThumbnail(sd);

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{ display:'flex', alignItems:'center', gap: 1.5, py: 1.25, px: 1.5,
        borderBottom:'1px solid #f0f0f0', '&:last-child':{ borderBottom:'none' },
        opacity: isDragging ? 0.5 : isActive ? 1 : 0.4,
        bgcolor: isDragging ? '#fff9f5' : 'transparent',
        '&:hover':{ bgcolor:'#fafafa' } }}>

      {/* Drag handle */}
      <Box {...attributes} {...listeners}
        sx={{ cursor:'grab', color:'#ccc', display:'flex', alignItems:'center',
          '&:active':{ cursor:'grabbing' }, '&:hover':{ color:'#999' } }}>
        <DragIndicatorIcon fontSize="small" />
      </Box>

      {/* Thumbnail / emoji */}
      {thumb ? (
        <Box sx={{ width: 52, height: 36, borderRadius: 1, overflow:'hidden', flexShrink:0, position:'relative', bgcolor:'#eee' }}>
          <img src={thumb.src} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
          {thumb.count > 1 && (
            <Box sx={{ position:'absolute', bottom:0, right:0, bgcolor:'rgba(0,0,0,0.65)',
              color:'white', fontSize:'0.6rem', fontWeight:700, px:0.5, lineHeight:'16px',
              borderRadius:'2px 0 0 0' }}>
              {thumb.count}
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ width: 52, height: 36, borderRadius: 1, bgcolor:'#f0f0f0', flexShrink:0,
          display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.25rem' }}>
          {getSlideEmoji(sd?.type)}
        </Box>
      )}

      {/* Title + meta */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography fontWeight="bold" noWrap sx={{ fontSize:'0.9rem' }}>
          {sd?.content?.title || sd?.title || sd?.type || 'Anslag'}
        </Typography>
        <Typography variant="caption" sx={{ color:'#aaa' }}>
          {sd?.createdByName || ''}{sd?.createdAt ? ` · ${sd.createdAt.toDate?.()?.toLocaleDateString('sv-SE') || ''}` : ''}
        </Typography>
      </Box>

      {/* Duration */}
      <Chip label={`${sr.duration || 10}s`} size="small" variant="outlined"
        sx={{ fontSize:'0.7rem', height:22, minWidth:38 }} />

      {/* Move-to-zone compact selector */}
      <Tooltip title="Flytta till annan zon">
        <Select
          value={sr.zone === 'SIDFOT' ? 'SIDFOT-1' : (sr.zone || 'HUVUDANSLAG')}
          size="small"
          onChange={e => handleChangeZone(sr.slideId, e.target.value)}
          sx={{ fontSize:'0.65rem', fontWeight:'bold', color:'white', height:26,
            bgcolor: ZONE_COLORS[sr.zone || 'HUVUDANSLAG'] || '#FF6B00',
            '& .MuiOutlinedInput-notchedOutline':{ border:'none' },
            '& .MuiSelect-icon':{ color:'rgba(255,255,255,0.8)', fontSize:'1rem' },
            '& .MuiSelect-select':{ py:0, px:1, lineHeight:'26px' },
            borderRadius:2 }}>
          {ZONE_OPTIONS.map(z => (
            <MenuItem key={z.value} value={z.value} sx={{ fontSize:'0.8rem' }}>{z.label}</MenuItem>
          ))}
        </Select>
      </Tooltip>

      {/* Preview */}
      <Tooltip title="Förhandsgranska">
        <IconButton size="small" onClick={() => onPreview(sr, sd)}>
          <PreviewIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Active toggle */}
      <Tooltip title={isActive ? 'Inaktivera' : 'Aktivera'}>
        <IconButton size="small" onClick={() => handleToggleActive(sr.slideId, isActive)}
          sx={{ color: isActive ? '#4caf50' : '#ccc' }}>
          {isActive ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      {/* Edit */}
      <Tooltip title="Redigera">
        <IconButton size="small"
          onClick={() => navigate(`/marketing/castn/casts/${selectedCastId}`, { state: { slideId: sr.slideId, slideType: sd?.type } })}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Delete */}
      <Tooltip title="Ta bort">
        <IconButton size="small" color="error" onClick={() => handleDeleteSlide(sr.slideId)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function NewCastDialog({ open, onClose, onSave }) {
  const [name, setName] = useState('');
  const saving = false;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Nytt Cast</DialogTitle>
      <DialogContent>
        <TextField autoFocus fullWidth label="Castnamn" value={name} onChange={e => setName(e.target.value)}
          sx={{ mt: 1 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Avbryt</Button>
        <Button variant="contained" disabled={!name.trim() || saving}
          sx={{ bgcolor:'#FF6B00', '&:hover':{ bgcolor:'#e55a00' } }}
          onClick={() => { onSave(name.trim()); setName(''); }}>
          Skapa
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function CastNCasts() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const companyId = userProfile?.companyId || '';
  const uid = currentUser?.uid || '';

  const [casts, setCasts] = useState([]);
  const [selectedCastId, setSelectedCastId] = useState('');
  const [slides, setSlides] = useState([]);  // { slideId, duration, zone, slideData? }
  const [slideDetails, setSlideDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [newCastOpen, setNewCastOpen] = useState(false);
  const [typePicker, setTypePicker] = useState(false);
  const [displays, setDisplays] = useState([]);
  const [previewSlide, setPreviewSlide] = useState(null); // { sr, sd }
  const [backgrounds, setBackgrounds]   = useState([]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  // Load casts
  useEffect(() => {
    if (!companyId) return;
    const q = query(collection(db, 'casts'), where('companyId', '==', companyId));
    const unsub = onSnapshot(q, snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setCasts(list);
      setSelectedCastId(prev => (!prev && list.length > 0 ? list[0].id : prev));
      setLoading(false);
    });
    return unsub;
  }, [companyId]);

  // Load background images
  useEffect(() => {
    if (!companyId) return;
    const q = query(collection(db, 'castBackgrounds'), where('companyId', '==', companyId));
    return onSnapshot(q, snap => setBackgrounds(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [companyId]);

  // Load displays for preview link
  useEffect(() => {
    if (!companyId) return;
    const q = query(collection(db, 'displays'), where('companyId', '==', companyId));
    const unsub = onSnapshot(q, snap => {
      setDisplays(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [companyId]);

  // Load slides for selected cast
  useEffect(() => {
    if (!selectedCastId) { setSlides([]); return; }
    const cast = casts.find(c => c.id === selectedCastId);
    if (!cast) return;
    const slideRefs = cast.slides || [];
    setSlides(slideRefs);
    // Load slide details (unconditionally, onSnapshot deduplicates)
    const unsubs = slideRefs.map(sr =>
      onSnapshot(doc(db, 'slides', sr.slideId), snap => {
        if (snap.exists()) {
          setSlideDetails(prev => ({ ...prev, [snap.id]: { id: snap.id, ...snap.data() } }));
        }
      })
    );
    return () => unsubs.forEach(u => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCastId, casts]);

  async function handleCreateCast(name) {
    try {
      const ref = await addDoc(collection(db, 'casts'), {
        name, slides: [], companyId, createdBy: uid, createdAt: serverTimestamp(),
      });
      setSelectedCastId(ref.id);
      setNewCastOpen(false);
    } catch(e) { console.error(e); }
  }

  async function handleDeleteSlide(slideId) {
    if (!window.confirm('Ta bort detta anslag?')) return;
    const cast = casts.find(c => c.id === selectedCastId);
    if (!cast) return;
    const newSlides = (cast.slides || []).filter(s => s.slideId !== slideId);
    await updateDoc(doc(db, 'casts', selectedCastId), { slides: newSlides });
    await deleteDoc(doc(db, 'slides', slideId));
  }

  async function handleToggleActive(slideId, currentlyActive) {
    await updateDoc(doc(db, 'slides', slideId), { active: !currentlyActive });
  }

  async function handleChangeZone(slideId, newZone) {
    // Update in cast slides array
    const cast = casts.find(c => c.id === selectedCastId);
    if (cast) {
      const updated = (cast.slides || []).map(s => s.slideId === slideId ? { ...s, zone: newZone } : s);
      await updateDoc(doc(db, 'casts', selectedCastId), { slides: updated });
    }
    // Update in slide document
    await updateDoc(doc(db, 'slides', slideId), { zone: newZone });
  }

  async function handleSaveBackground(url) {
    if (!selectedCastId) return;
    await updateDoc(doc(db, 'casts', selectedCastId), { backgroundImageUrl: url || null });
  }

  async function handleSaveTheme(themeId) {
    if (!selectedCastId) return;
    await updateDoc(doc(db, 'casts', selectedCastId), { theme: themeId });
  }

  async function handleSaveLayout(field, value) {
    if (!selectedCastId) return;
    await updateDoc(doc(db, 'casts', selectedCastId), { [field]: value });
  }

  async function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const cast = casts.find(c => c.id === selectedCastId);
    if (!cast) return;
    const arr = [...(cast.slides || [])];
    const oldIdx = arr.findIndex(s => s.slideId === active.id);
    const newIdx = arr.findIndex(s => s.slideId === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = arrayMove(arr, oldIdx, newIdx);
    await updateDoc(doc(db, 'casts', selectedCastId), { slides: reordered });
  }

  const selectedCast = casts.find(c => c.id === selectedCastId);
  const connectedDisplay = displays.find(d => d.castId === selectedCastId);

  function getSlideEmoji(type) {
    const all = SLIDE_TYPES.flatMap(s => s.items);
    return all.find(i => i.type === type)?.emoji || '📄';
  }

  return (
    <Box sx={{ minHeight:'100vh', bgcolor:'#f5f5f5' }}>
      {/* Header */}
      <Box sx={{ bgcolor:'#1a1a1a', color:'white', px: 3, py: 2, display:'flex', alignItems:'center', gap: 2 }}>
        <LagbasLogo size={32} />
        <Typography variant="h6" fontWeight="bold">
          Cast<span style={{ color:'#FF6B00' }}>N</span>
          <span style={{ color:'#aaa', fontWeight:400, fontSize:'0.9rem', ml: 1 }}>– Hantera Casts</span>
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<ArrowBackIcon />} sx={{ color:'#aaa' }} onClick={() => navigate(-1)}>
          Tillbaka
        </Button>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Cast selector bar */}
        <Paper sx={{ p: 2, mb: 3, display:'flex', alignItems:'center', gap: 2 }}>
          <TextField select label="Cast" value={selectedCastId} onChange={e => setSelectedCastId(e.target.value)}
            sx={{ minWidth: 240 }} size="small">
            {casts.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            {casts.length === 0 && <MenuItem disabled>Inga casts än</MenuItem>}
          </TextField>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setNewCastOpen(true)}
            sx={{ borderColor:'#FF6B00', color:'#FF6B00', '&:hover':{ borderColor:'#e55a00', bgcolor:'rgba(255,107,0,0.05)' } }}>
            Nytt Cast
          </Button>
          <Box sx={{ flex: 1 }} />
          {selectedCast && (
            <Tooltip title={connectedDisplay ? `Förhandsvisa på ${connectedDisplay.name}` : 'Ingen skärm kopplad till detta cast'}>
              <span>
                <Button startIcon={<PreviewIcon />} variant="outlined"
                  disabled={!connectedDisplay}
                  onClick={() => navigate(`/display/${connectedDisplay?.token}`)}
                  sx={{ borderColor:'#2196F3', color:'#2196F3' }}>
                  FÖRHANDSVISA
                </Button>
              </span>
            </Tooltip>
          )}
        </Paper>

        {loading && <Box sx={{ textAlign:'center', py: 6 }}><CircularProgress sx={{ color:'#FF6B00' }} /></Box>}

        {!loading && selectedCast && (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">{selectedCast.name}</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setTypePicker(true)}
                sx={{ bgcolor:'#FF6B00', '&:hover':{ bgcolor:'#e55a00' } }}>
                NYTT ANSLAG +
              </Button>
            </Box>

            {/* Theme picker */}
            <Box sx={{ display:'flex', alignItems:'center', gap: 1.5, mb: 2, flexWrap:'wrap' }}>
              <Typography variant="caption" sx={{ color:'#888', mr: 0.5 }}>Tema:</Typography>
              {CAST_THEMES.map(t => {
                const active = (selectedCast.theme || 'tikab') === t.id;
                return (
                  <Box key={t.id} onClick={() => handleSaveTheme(t.id)}
                    sx={{ display:'flex', alignItems:'center', gap: 0.75, px: 1.5, py: 0.5, borderRadius: 2,
                      cursor:'pointer', border: active ? `2px solid ${t.accent}` : '2px solid #e0e0e0',
                      bgcolor: active ? `${t.accent}15` : 'transparent',
                      '&:hover':{ borderColor: t.accent } }}>
                    <Box sx={{ width: 12, height: 12, borderRadius:'50%', bgcolor: t.accent }} />
                    <Typography variant="caption" fontWeight={active ? 700 : 400}>{t.label}</Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Background image picker */}
            <Box sx={{ display:'flex', alignItems:'center', gap: 1.5, mb: 3, flexWrap:'wrap' }}>
              <Typography variant="caption" sx={{ color:'#888', mr: 0.5 }}>Bakgrund:</Typography>
              {/* No background option */}
              <Box onClick={() => handleSaveBackground('')}
                sx={{ width: 48, height: 32, borderRadius: 1, bgcolor:'#1a1a1a', cursor:'pointer',
                  border: !selectedCast.backgroundImageUrl ? '3px solid #FF6B00' : '2px solid transparent',
                  display:'flex', alignItems:'center', justifyContent:'center' }}>
                <Typography sx={{ color:'#555', fontSize:'0.6rem' }}>Ingen</Typography>
              </Box>
              {backgrounds.map(bg => (
                <Tooltip key={bg.id} title={bg.name}>
                  <Box onClick={() => handleSaveBackground(bg.url)}
                    sx={{ width: 64, height: 36, borderRadius: 1, overflow:'hidden', cursor:'pointer', flexShrink: 0,
                      border: selectedCast.backgroundImageUrl === bg.url ? '3px solid #FF6B00' : '2px solid transparent',
                      '&:hover':{ border:'2px solid #FF6B00' } }}>
                    <img src={bg.url} alt={bg.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                  </Box>
                </Tooltip>
              ))}
              {backgrounds.length === 0 && (
                <Typography variant="caption" sx={{ color:'#bbb', fontStyle:'italic' }}>
                  Inga bakgrundsbilder – lägg till på{' '}
                  <span style={{ color:'#FF6B00', cursor:'pointer' }}
                    onClick={() => navigate('/marketing/castn/backgrounds')}>
                    Bakgrundsbilder-sidan
                  </span>
                </Typography>
              )}
            </Box>

            {/* Layout configuration */}
            <Box sx={{ mb: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="caption" sx={{ color:'#888', mb: 1.5, display:'block', fontWeight: 600, letterSpacing: 1, textTransform:'uppercase' }}>
                Layout & visning
              </Typography>
              <Box sx={{ display:'flex', flexWrap:'wrap', gap: 3 }}>
                {/* Display layout */}
                <Box sx={{ minWidth: 200 }}>
                  <Typography variant="caption" sx={{ color:'#666', mb: 0.75, display:'block' }}>Skärmlayout</Typography>
                  <ToggleButtonGroup exclusive size="small"
                    value={selectedCast.displayLayout || 'fullscreen'}
                    onChange={(_, v) => v && handleSaveLayout('displayLayout', v)}>
                    {[
                      { v:'fullscreen',  l:'Helskärm' },
                      { v:'two-zone',    l:'Huvud+Sido' },
                      { v:'three-zone',  l:'3 zoner' },
                      { v:'bottom-bar',  l:'Huvud+Bot' },
                    ].map(o => (
                      <ToggleButton key={o.v} value={o.v}
                        sx={{ fontSize:'0.75rem', px: 1.5,
                          '&.Mui-selected':{ bgcolor:'rgba(255,107,0,0.12)', color:'#FF6B00', fontWeight:700 } }}>
                        {o.l}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>

                {/* Panel style */}
                <Box>
                  <Typography variant="caption" sx={{ color:'#666', mb: 0.75, display:'block' }}>Panelstil</Typography>
                  <ToggleButtonGroup exclusive size="small"
                    value={selectedCast.panelStyle || 'classic'}
                    onChange={(_, v) => v && handleSaveLayout('panelStyle', v)}>
                    {[{ v:'classic', l:'Klassisk' }, { v:'glass', l:'Glas' }].map(o => (
                      <ToggleButton key={o.v} value={o.v}
                        sx={{ fontSize:'0.75rem', px: 1.5,
                          '&.Mui-selected':{ bgcolor:'rgba(255,107,0,0.12)', color:'#FF6B00', fontWeight:700 } }}>
                        {o.l}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>

                {/* Sidfot columns */}
                {(selectedCast.displayLayout === 'three-zone' || selectedCast.displayLayout === 'bottom-bar') && (
                  <Box>
                    <Typography variant="caption" sx={{ color:'#666', mb: 0.75, display:'block' }}>Sidfot – kolumner</Typography>
                    <ToggleButtonGroup exclusive size="small"
                      value={selectedCast.sidfotColumns || 1}
                      onChange={(_, v) => v && handleSaveLayout('sidfotColumns', v)}>
                      {[1,2,3,4].map(n => (
                        <ToggleButton key={n} value={n}
                          sx={{ fontSize:'0.75rem', minWidth: 36,
                            '&.Mui-selected':{ bgcolor:'rgba(255,107,0,0.12)', color:'#FF6B00', fontWeight:700 } }}>
                          {n}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Box>
                )}

                {/* Sido rows */}
                {(selectedCast.displayLayout === 'two-zone' || selectedCast.displayLayout === 'three-zone') && (
                  <Box>
                    <Typography variant="caption" sx={{ color:'#666', mb: 0.75, display:'block' }}>Sidofält – rader</Typography>
                    <ToggleButtonGroup exclusive size="small"
                      value={selectedCast.sidoRows || 1}
                      onChange={(_, v) => v && handleSaveLayout('sidoRows', v)}>
                      {[1,2,3].map(n => (
                        <ToggleButton key={n} value={n}
                          sx={{ fontSize:'0.75rem', minWidth: 36,
                            '&.Mui-selected':{ bgcolor:'rgba(255,107,0,0.12)', color:'#FF6B00', fontWeight:700 } }}>
                          {n}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Box>
                )}

                {/* Glass options: gap + radius */}
                {selectedCast.panelStyle === 'glass' && (
                  <>
                    <Box>
                      <Typography variant="caption" sx={{ color:'#666', mb: 0.75, display:'block' }}>Avstånd (px)</Typography>
                      <ToggleButtonGroup exclusive size="small"
                        value={selectedCast.panelGap ?? 8}
                        onChange={(_, v) => v !== null && handleSaveLayout('panelGap', v)}>
                        {[4,8,12,20].map(n => (
                          <ToggleButton key={n} value={n}
                            sx={{ fontSize:'0.75rem', minWidth: 36,
                              '&.Mui-selected':{ bgcolor:'rgba(255,107,0,0.12)', color:'#FF6B00', fontWeight:700 } }}>
                            {n}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color:'#666', mb: 0.75, display:'block' }}>Radie (px)</Typography>
                      <ToggleButtonGroup exclusive size="small"
                        value={selectedCast.panelRadius ?? 12}
                        onChange={(_, v) => v !== null && handleSaveLayout('panelRadius', v)}>
                        {[0,6,12,20,32].map(n => (
                          <ToggleButton key={n} value={n}
                            sx={{ fontSize:'0.75rem', minWidth: 36,
                              '&.Mui-selected':{ bgcolor:'rgba(255,107,0,0.12)', color:'#FF6B00', fontWeight:700 } }}>
                            {n}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color:'#666', mb: 0.75, display:'block' }}>
                        Genomskinlighet – {Math.round((1 - (selectedCast.panelOpacity ?? 0.55)) * 100)}%
                      </Typography>
                      <Box sx={{ px: 0.5 }}>
                        <input type="range" min={0} max={0.95} step={0.05}
                          value={selectedCast.panelOpacity ?? 0.55}
                          onChange={e => handleSaveLayout('panelOpacity', parseFloat(e.target.value))}
                          style={{ width: 160, accentColor:'#FF6B00' }} />
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color:'#666', mb: 0.75, display:'block' }}>
                        Blur – {selectedCast.panelBlur ?? 6}px
                      </Typography>
                      <Box sx={{ px: 0.5 }}>
                        <input type="range" min={0} max={24} step={1}
                          value={selectedCast.panelBlur ?? 6}
                          onChange={e => handleSaveLayout('panelBlur', parseInt(e.target.value, 10))}
                          style={{ width: 160, accentColor:'#FF6B00' }} />
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color:'#666', mb: 0.75, display:'block' }}>Panelfärg</Typography>
                      <input type="color"
                        value={selectedCast.panelColor ?? '#000000'}
                        onChange={e => handleSaveLayout('panelColor', e.target.value)}
                        style={{ width: 48, height: 32, border:'none', borderRadius: 4,
                          background:'none', cursor:'pointer', padding: 0 }} />
                    </Box>
                  </>
                )}
              </Box>
            </Box>

            {slides.length === 0 && (
              <Box sx={{ textAlign:'center', py: 6, color:'#aaa' }}>
                <Typography>Inga anslag ännu. Klicka "NYTT ANSLAG +" för att lägga till.</Typography>
              </Box>
            )}

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={slides.map(s => s.slideId)} strategy={verticalListSortingStrategy}>
                {ZONE_GROUPS.map(group => {
                  const groupSlides = slides.filter(sr =>
                    group.zones.includes(sr.zone || 'HUVUDANSLAG')
                  );
                  if (groupSlides.length === 0) return null;
                  return (
                    <Box key={group.id} sx={{ mb: 2 }}>
                      {/* Zone section header */}
                      <Box sx={{
                        display:'flex', alignItems:'center', gap: 1,
                        bgcolor: group.color, color:'white',
                        px: 2, py: 0.85, borderRadius:'6px 6px 0 0',
                      }}>
                        <Typography sx={{ fontWeight:800, fontSize:'0.75rem', letterSpacing:1.5, textTransform:'uppercase', flex:1 }}>
                          {group.label}
                        </Typography>
                        <Typography sx={{ fontSize:'0.7rem', opacity:0.75 }}>
                          {groupSlides.length} anslag
                        </Typography>
                      </Box>
                      {/* Slides in this zone */}
                      <Box sx={{ border:`1px solid ${group.color}40`, borderTop:'none', borderRadius:'0 0 6px 6px' }}>
                        {groupSlides.map(sr => (
                          <SortableSlideRow
                            key={sr.slideId}
                            sr={sr}
                            slideDetails={slideDetails}
                            getSlideEmoji={getSlideEmoji}
                            navigate={navigate}
                            selectedCastId={selectedCastId}
                            handleDeleteSlide={handleDeleteSlide}
                            handleToggleActive={handleToggleActive}
                            handleChangeZone={handleChangeZone}
                            onPreview={(sr, sd) => setPreviewSlide({ sr, sd })}
                          />
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </SortableContext>
            </DndContext>
          </Paper>
        )}

        {!loading && !selectedCast && (
          <Box sx={{ textAlign:'center', py: 8, color:'#aaa' }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Inga casts ännu</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewCastOpen(true)}
              sx={{ bgcolor:'#FF6B00', '&:hover':{ bgcolor:'#e55a00' } }}>
              Skapa ditt första Cast
            </Button>
          </Box>
        )}
      </Container>

      <NewCastDialog open={newCastOpen} onClose={() => setNewCastOpen(false)} onSave={handleCreateCast} />
      <SlideTypePicker open={typePicker} onClose={() => setTypePicker(false)}
        onSelect={type => navigate(`/marketing/castn/casts/${selectedCastId}`, { state: { slideType: type, castId: selectedCastId } })} />
      <SlidePreviewDialog
        open={Boolean(previewSlide)}
        onClose={() => setPreviewSlide(null)}
        slide={previewSlide?.sd}
        slideRef={previewSlide?.sr}
      />
    </Box>
  );
}
